/**
 * HandTremorDetector — measure micro-vibration amplitude on the 5
 * fingertip landmarks within a sliding window.
 *
 * 의학 essential tremor 4–12Hz 중 useHandLandmarker가 ~15fps로 동작하므로
 * Nyquist 한계 7.5Hz, 신뢰 가능한 측정 대역은 4–7Hz. 1초 윈도우(~15
 * sample)에 대해 fingertip(idx 4/8/12/16/20) 좌표의 표준편차를 평균
 * 내어 amplitude(px)를 산출하고, X축 zero-crossing 빈도로 dominant
 * frequency를 추정한다.
 *
 * 출력은 단정 boolean이 아닌 amplitude/freq/count/confidence 인터페이스
 * — AI Proctor 6원칙 #2(오탐 전제)와 정합. 임계값은 호출자가 결정.
 */

export interface HandLandmark { x: number; y: number; z?: number }

export interface TremorMetric {
  /** Average per-fingertip stddev × image side length, in image pixels. */
  amplitudePx: number;
  /** Dominant frequency in Hz (zero-crossing rate of high-pass-like signal). */
  freqHz: number;
  /** Number of samples accumulated in the window (for confidence weighting). */
  count: number;
  /** 0..1 — saturates when amplitude crosses tremorAmplitudeThresh. */
  confidence: number;
}

const ZERO: TremorMetric = {
  amplitudePx: 0, freqHz: 0, count: 0, confidence: 0,
};

const FINGERTIP_IDX = [4, 8, 12, 16, 20] as const;

/**
 * Stateful detector. Call `update(landmarks, now, imageSidePx)` per frame.
 *
 * @param windowMs sliding window duration. Default 1000ms.
 * @param amplitudeThreshPx amplitude that maps to confidence=1.
 *   Below this it ramps linearly. Tune empirically per use case.
 */
export class HandTremorDetector {
  private readonly windowMs: number;
  private readonly amplitudeThreshPx: number;
  // Per-fingertip ring of {x, y, t} samples within the window.
  private rings: Array<Array<{ x: number; y: number; t: number }>> = FINGERTIP_IDX.map(() => []);

  constructor(windowMs = 1000, amplitudeThreshPx = 4) {
    this.windowMs = windowMs;
    this.amplitudeThreshPx = amplitudeThreshPx;
  }

  reset(): void {
    this.rings = FINGERTIP_IDX.map(() => []);
  }

  update(
    landmarks: HandLandmark[] | null | undefined,
    now: number,
    imageSidePx: number,
  ): TremorMetric {
    if (!landmarks || landmarks.length < 21) {
      // Hand lost → reset rings. Tremor is a **current** signal; carrying
      // stale samples from before disappearance produces misleading metrics
      // (frame_005 case: hand_count=0 but amplitude=48px reported).
      this.reset();
      return ZERO;
    }
    for (let i = 0; i < FINGERTIP_IDX.length; i++) {
      const lm = landmarks[FINGERTIP_IDX[i]];
      if (!lm) continue;
      this.rings[i].push({ x: lm.x, y: lm.y, t: now });
    }
    this.prune(now);
    return this.compute(imageSidePx);
  }

  private prune(now: number): void {
    const cutoff = now - this.windowMs;
    for (const ring of this.rings) {
      while (ring.length > 0 && ring[0].t < cutoff) ring.shift();
    }
  }

  private compute(imageSidePx: number): TremorMetric {
    const minSamples = 5;
    // Maximum amplitude (px) considered "tremor". Beyond this the signal is
    // dominated by gross motion (writing strokes, hand reaching), not tremor.
    // Above this threshold we report amplitudePx but reduce confidence to 0.
    const motionAmplitudePx = imageSidePx * 0.020;  // ~25px on 1280

    let totalCount = 0;
    let stdSum = 0;
    let zcSum = 0;
    let usedFingers = 0;
    let totalSpan = 0;

    for (const ring of this.rings) {
      if (ring.length < minSamples) continue;

      // Detrending — subtract a moving average to isolate high-frequency
      // component (tremor) from low-frequency drift (intentional motion).
      // Use a 3-sample triangular kernel as a cheap low-pass.
      const detrendX: number[] = [];
      const detrendY: number[] = [];
      const W = 3;
      for (let k = 0; k < ring.length; k++) {
        let sumX = 0, sumY = 0, n = 0;
        for (let j = Math.max(0, k - W); j <= Math.min(ring.length - 1, k + W); j++) {
          sumX += ring[j].x; sumY += ring[j].y; n++;
        }
        detrendX.push(ring[k].x - sumX / n);
        detrendY.push(ring[k].y - sumY / n);
      }

      // Variance of the detrended (high-freq) signal — true tremor amplitude.
      let vx = 0, vy = 0;
      for (let k = 0; k < ring.length; k++) {
        vx += detrendX[k] ** 2;
        vy += detrendY[k] ** 2;
      }
      vx /= ring.length; vy /= ring.length;
      const std = Math.sqrt(vx + vy);

      // Zero-crossings on detrended X — dominant freq estimate.
      let zc = 0;
      let prev = detrendX[0];
      for (let k = 1; k < detrendX.length; k++) {
        const cur = detrendX[k];
        if ((prev < 0 && cur >= 0) || (prev > 0 && cur <= 0)) zc++;
        prev = cur;
      }

      stdSum += std;
      zcSum += zc;
      totalSpan += (ring[ring.length - 1].t - ring[0].t);
      totalCount += ring.length;
      usedFingers++;
    }
    if (usedFingers === 0) return ZERO;

    const amplitudePx = (stdSum / usedFingers) * imageSidePx;
    const meanZc = zcSum / usedFingers;
    const meanSpanSec = (totalSpan / usedFingers) / 1000;
    const freqHz = meanSpanSec > 0 ? (meanZc / meanSpanSec) / 2 : 0;

    // Confidence:
    //   - 0 when amplitude < threshold (no signal)
    //   - 1 when amplitude in [threshold, motionAmplitude] (clear tremor)
    //   - 0 again when amplitude > motionAmplitude (it's gross motion, not tremor)
    let confidence = 0;
    if (amplitudePx >= this.amplitudeThreshPx && amplitudePx <= motionAmplitudePx) {
      confidence = Math.min(1, (amplitudePx - this.amplitudeThreshPx) /
                            (motionAmplitudePx - this.amplitudeThreshPx) + 0.3);
    } else if (amplitudePx > motionAmplitudePx) {
      confidence = 0;  // motion, not tremor
    }
    // Also require freq in tremor band (3~8 Hz) for confidence to count.
    if (freqHz < 3 || freqHz > 8) confidence *= 0.3;

    return {
      amplitudePx,
      freqHz,
      count: Math.round(totalCount / usedFingers),
      confidence,
    };
  }
}
